// Default kosong = panggil API relatif (same-origin) — cocok untuk deploy 1 app
// (backend menyajikan frontend). Bisa di-override dengan REACT_APP_API_URL bila
// backend & frontend dipisah. Saat dev lokal, "proxy" di package.json meneruskan
// request /api ke http://localhost:5000.
const API_BASE = process.env.REACT_APP_API_URL || "";
const BRANCH_KEY = "kip_portal_branch";
const TOKEN_KEY = "kip_portal_token";

// ── Token identitas (Tahap 6): dikirim di header Authorization pada SETIAP request ──
// Diinit dari localStorage saat modul dimuat (anti-race saat reload).
let AUTH_TOKEN = null;
try {
  AUTH_TOKEN = localStorage.getItem(TOKEN_KEY) || null;
} catch {
  AUTH_TOKEN = null;
}

export function setAuthToken(token) {
  AUTH_TOKEN = token || null;
  try {
    if (AUTH_TOKEN) localStorage.setItem(TOKEN_KEY, AUTH_TOKEN);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* abaikan */
  }
}

export function getAuthToken() {
  return AUTH_TOKEN;
}

// Callback saat server menolak sesi (401) — dipasang App.js utk auto-logout.
let onUnauthorized = null;
export function setOnUnauthorized(fn) {
  onUnauthorized = fn;
}

// ── Multi-cabang: cabang aktif dikirim di header "X-Branch-Id" pada SETIAP request ──
// Satu choke point (fungsi request) → semua GET & mutasi otomatis ter-scope cabang.
// Diinit dari localStorage saat modul dimuat agar header sudah benar bahkan sebelum
// App sempat menyetelnya (hindari race saat reload). null → tanpa header (legacy/global).
let ACTIVE_BRANCH = null;
try {
  const saved = Number(localStorage.getItem(BRANCH_KEY));
  ACTIVE_BRANCH = Number.isInteger(saved) && saved > 0 ? saved : null;
} catch {
  ACTIVE_BRANCH = null;
}

export function setActiveBranch(id) {
  const n = Number(id);
  ACTIVE_BRANCH = Number.isInteger(n) && n > 0 ? n : null;
  try {
    if (ACTIVE_BRANCH) localStorage.setItem(BRANCH_KEY, String(ACTIVE_BRANCH));
    else localStorage.removeItem(BRANCH_KEY);
  } catch {
    /* abaikan storage error */
  }
}

export function getActiveBranch() {
  return ACTIVE_BRANCH;
}

// ── Kunci-tulis mode "Semua Cabang" (super admin belum memfilter) ──
// Mode ini hanya-baca (keputusan produk): mutasi per-cabang diblok sampai cabang
// tertentu dipilih. Operasi GLOBAL (kelola user/cabang, approve request) tetap boleh.
let WRITE_LOCK = false;
export function setWriteLock(v) {
  WRITE_LOCK = !!v;
}

function isGlobalWriteAllowed(path) {
  return (
    path.startsWith("/api/admin/users") ||     // kelola akun (cabang di-set di modal)
    path.startsWith("/api/branches") ||        // kelola cabang
    path.startsWith("/api/admin/edit-requests") || // approve/reject (by id)
    path.startsWith("/api/auth/")              // login
  );
}

async function request(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  if (WRITE_LOCK && method !== "GET" && !isGlobalWriteAllowed(path)) {
    throw new Error(
      "Mode Semua Cabang bersifat hanya-baca. Pilih cabang tertentu di kanan atas untuk melakukan perubahan."
    );
  }

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
      ...(ACTIVE_BRANCH ? { "X-Branch-Id": String(ACTIVE_BRANCH) } : {}),
      ...(options.headers || {})
    },
    ...options
  });

  // Sesi tidak valid/berakhir (enforcement Tahap 6c) → auto-logout. Kecuali endpoint
  // auth (login gagal juga 401 tapi bukan "sesi berakhir").
  if (res.status === 401 && onUnauthorized && !path.startsWith("/api/auth")) {
    onUnauthorized();
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    // Isi jawaban server ikut dibawa: penolakan yang meminta keputusan (409 + `kode`)
    // butuh lebih dari sekadar pesannya, mis. murid yang masih di kelas lain berjam sama.
    const err = new Error(data.message || "API request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function buildQuery(params) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });

  return searchParams.toString();
}

export const api = {
  // AUTH
  login(email, password, keepSignedIn = true) {
    return request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, keepSignedIn })
    });
  },

  logout() {
    return request("/api/auth/logout", { method: "POST" });
  },

  // Data akun SEGAR (peran & daftar cabang). Daftar cabang dari login bisa basi: sesi
  // bertahan sampai 30 hari, sedangkan cabang tempat ia ditunjuk menggantikan kelas bisa
  // bertambah kapan saja.
  getMe() {
    return request("/api/auth/me");
  },

  // BRANCHES (multi-cabang)
  getBranches() {
    return request("/api/branches");
  },

  getAllBranchesAdmin() {
    return request("/api/branches/all");
  },

  createBranch(payload) {
    return request("/api/branches", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  updateBranch(id, payload) {
    return request(`/api/branches/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  // USERS
  getUsers(branchId) {
    // branchId: angka → cabang itu; "" → semua; undefined → cabang aktif (header).
    const q =
      branchId === undefined || branchId === null
        ? ""
        : `?branchId=${encodeURIComponent(branchId)}`;
    return request(`/api/admin/users${q}`);
  },

  saveUser(payload) {
    return request("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  deleteUser(id) {
    return request(`/api/admin/users/${id}`, {
      method: "DELETE"
    });
  },

  // STUDENTS
  getStudents(userEmail) {
    const query = buildQuery({ userEmail });
    return request(`/api/students${query ? `?${query}` : ""}`);
  },

  getAllStudents() {
    return request("/api/students/all");
  },

  getTrainerList() {
    return request("/api/students/trainers");
  },

  addStudent(payload) {
    return request("/api/students", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  updateStudent(id, payload) {
    return request(`/api/students/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  updateStudentInline(id, field, value) {
    return request(`/api/students/${id}/inline`, {
      method: "PATCH",
      body: JSON.stringify({ field, value })
    });
  },

  deleteStudent(id) {
    return request(`/api/students/${id}`, {
      method: "DELETE"
    });
  },

  // Ganti ID murid: pratinjau (apa yang ikut pindah & apakah ID barunya boleh),
  // lalu simpan. ID dipindahkan di semua tabel yang menyimpannya sekaligus.
  previewChangeStudentId(id, newId) {
    const query = newId ? `?newId=${encodeURIComponent(newId)}` : "";
    return request(`/api/students/${encodeURIComponent(id)}/change-id${query}`);
  },

  changeStudentId(id, newId) {
    return request(`/api/students/${encodeURIComponent(id)}/change-id`, {
      method: "POST",
      body: JSON.stringify({ newId })
    });
  },

  // ATTENDANCE
  getAttendanceForDate({ userEmail, year, month, day, coveredTrainer }) {
    const query = buildQuery({
      userEmail,
      year,
      month,
      day,
      coveredTrainer
    });

    return request(`/api/attendance/date?${query}`);
  },

  submitAttendanceRecord(payload) {
    return request("/api/attendance/submit", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  updatePunctuality(payload) {
    return request("/api/attendance/punctuality", {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  completeSession(payload) {
    return request("/api/attendance/complete-session", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  unmarkAttendance(payload) {
    return request("/api/attendance/unmark", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  createEditRequest(payload) {
    return request("/api/attendance/edit-request", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  addStudentToSession(payload) {
    return request("/api/attendance/add-student", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  updateAdditionPunctuality(payload) {
    return request("/api/attendance/add-student/punctuality", {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  cancelStudentAddition(payload) {
    return request("/api/attendance/add-student/cancel", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  getEditRequests(status) {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    return request(`/api/admin/edit-requests${query}`);
  },

  reviewEditRequest({ id, status, reviewedBy, type }) {
    return request(`/api/admin/edit-requests/${id}`, {
      method: "PUT",
      body: JSON.stringify({ status, reviewedBy, type })
    });
  },

  getTrainerRecap({ userEmail, year, month, session }) {
    const query = buildQuery({
      userEmail,
      year,
      month,
      session
    });

    return request(`/api/attendance/trainer-recap?${query}`);
  },

  getAdminRecapData({ year, month, trainer, session, classId }) {
    const query = buildQuery({
      year,
      month,
      trainer,
      session,
      classId
    });

    return request(`/api/attendance/admin-recap?${query}`);
  },

  // SETTINGS
  getSystemSettings() {
   return request("/api/settings");
  },

  updateSystemSetting({ sheetName, row, col, val }) {
  return request("/api/settings", {
    method: "PUT",
    body: JSON.stringify({ sheetName, row, col, val })
   });
  },

  addSystemSettingRow({ sheetName, rowData }) {
  return request("/api/settings/row", {
    method: "POST",
    body: JSON.stringify({ sheetName, rowData })
   });
  },

  // ── Staff Payroll ─ tarif per sesi (Data Setting) ─────────────────────
  getStaffRates() {
    return request("/api/staff-payroll/rates");
  },

  addStaffPosition(position) {
    return request("/api/staff-payroll/rates/position", {
      method: "POST",
      body: JSON.stringify({ position })
    });
  },

  setStaffRate({ position, session, rate }) {
    return request("/api/staff-payroll/rates", {
      method: "PUT",
      body: JSON.stringify({ position, session, rate })
    });
  },

  deleteStaffPosition(position) {
    return request("/api/staff-payroll/rates/position", {
      method: "DELETE",
      body: JSON.stringify({ position })
    });
  },

  // ── Staff Payroll ─ penunjukan, centang, rekap ─────────────────────
  getStaffList() {
    return request("/api/staff-payroll/staff");
  },

  setStaffPosition({ userId, staffPosition }) {
    return request("/api/staff-payroll/staff", {
      method: "PUT",
      body: JSON.stringify({ userId, staffPosition })
    });
  },

  getStaffSessions() {
    return request("/api/staff-payroll/sessions");
  },

  setStaffBlocks({ userId, sessions }) {
    return request("/api/staff-payroll/blocks", {
      method: "PUT",
      body: JSON.stringify({ userId, sessions })
    });
  },

  getStaffMarks(date) {
    return request(`/api/staff-payroll/marks?date=${encodeURIComponent(date)}`);
  },

  setStaffMark({ userId, date, session, checked }) {
    return request("/api/staff-payroll/marks", {
      method: "POST",
      body: JSON.stringify({ userId, date, session, checked })
    });
  },

  getStaffRecap({ year, month }) {
    return request(`/api/staff-payroll/recap?year=${year}&month=${month}`);
  },

  refreshStaffRates({ year, month }) {
    return request("/api/staff-payroll/marks/refresh-rates", {
      method: "POST",
      body: JSON.stringify({ year, month })
    });
  },

  lockStaffMonth({ year, month }) {
    return request("/api/staff-payroll/lock", {
      method: "POST",
      body: JSON.stringify({ year, month })
    });
  },

  unlockStaffMonth({ year, month }) {
    return request("/api/staff-payroll/lock", {
      method: "DELETE",
      body: JSON.stringify({ year, month })
    });
  },

  // ── Kunci payroll BERSAMA (gaji trainer + staff), per cabang per bulan ──
  // Kunci yang SAMA dengan lockStaffMonth di atas — hanya pintunya yang lain.
  getPayrollLock({ year, month }) {
    return request(`/api/payroll-lock?${buildQuery({ year, month })}`);
  },

  lockPayroll({ year, month }) {
    return request("/api/payroll-lock", {
      method: "POST",
      body: JSON.stringify({ year, month })
    });
  },

  unlockPayroll({ year, month }) {
    return request("/api/payroll-lock", {
      method: "DELETE",
      body: JSON.stringify({ year, month })
    });
  },

  getSalaryPositions() {
  return request("/api/settings/salary-positions");
  },

  // SALARY
  getAdminSalaryData({ year, month, trainer }) {
  const query = buildQuery({ year, month, trainer });
   return request(`/api/salary/admin?${query}`);
  },

  updateSalaryPositionAndRecalculate(payload) {
  return request("/api/salary/position-recalculate", {
    method: "PUT",
    body: JSON.stringify(payload)
   });
  },

  // Fase 2c-3: bandingkan masukan gaji lewat nama vs lewat akun (super admin).
  compareCoverage({ year, month }) {
    return request(`/api/salary/coverage-compare?year=${year}&month=${month}`);
  },

  // ── To-Do List kelas ──
  getTodoOptions(classId) {
    return request(`/api/todo/options?classId=${encodeURIComponent(classId)}`);
  },

  getTodoActivity(id) {
    return request(`/api/todo/activities/${encodeURIComponent(id)}`);
  },

  getTodo(classId, date) {
    return request(`/api/todo?classId=${encodeURIComponent(classId)}&date=${encodeURIComponent(date)}`);
  },

  getTodoTerakhir(classId, before) {
    return request(`/api/todo/terakhir?classId=${encodeURIComponent(classId)}&before=${encodeURIComponent(before)}`);
  },

  simpanTodo(payload) {
    return request("/api/todo", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  // "Apakah aku sedang dalam masa pengawasan pada tanggal ini?" — akun dari token.
  getPengawasanSaya(date) {
    return request(`/api/todo/pengawasan?date=${encodeURIComponent(date)}`);
  },

  getFinalSalaryData({ year, month }) {
   const query = buildQuery({ year, month });
   return request(`/api/salary/final?${query}`);
  },

  getTrainerTransactions({ year, month, trainer, type }) {
   const query = buildQuery({ year, month, trainer, type });
   return request(`/api/salary/transactions?${query}`);
  },

  addSalaryTransaction(payload) {
   return request("/api/salary/transactions", {
     method: "POST",
     body: JSON.stringify(payload)
    });
  },

  deleteSalaryTransaction(id) {
   return request(`/api/salary/transactions/${id}`, {
     method: "DELETE"
    });
  },

  getTrainerPayslipData({ userEmail, year, month }) {
   const query = buildQuery({ userEmail, year, month });
   return request(`/api/salary/trainer-payslip?${query}`);
  },

  // ADMIN DASHBOARD
  // ── Reset absensi satu kartu (kelas · tanggal · sesi) ────────────────────────────
  previewAttendanceReset({ date, classIds }) {
    return request("/api/attendance-reset/preview", {
      method: "POST",
      body: JSON.stringify({ date, classIds })
    });
  },

  resetAttendance({ date, classIds, reason }) {
    return request("/api/attendance-reset", {
      method: "POST",
      body: JSON.stringify({ date, classIds, reason })
    });
  },

  listAttendanceResets(date) {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    return request(`/api/attendance-reset${query}`);
  },

  undoAttendanceReset(id) {
    return request(`/api/attendance-reset/${encodeURIComponent(id)}/undo`, {
      method: "POST",
      body: JSON.stringify({})
    });
  },

  getDashboardSchedules(date) {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    return request(`/api/admin/dashboard-schedules${query}`);
  },

  assignCoverage(payload) {
    return request("/api/admin/assign-coverage", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  cancelCoverage(payload) {
    return request("/api/admin/cancel-coverage", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  // ADMIN DASHBOARD schedule helpers
  assignScheduleToday({ todayISO_GMT7 }) {
    return request("/api/admin/assign-schedule-today", {
      method: "POST",
      body: JSON.stringify({ todayISO_GMT7 })
    });
  },

  getSalaryAdjustments({ month, year, trainer }) {
    const query = buildQuery({ month, year, trainer });
    return request(`/api/admin/salary-adjustments?${query}`);
  },

  // TRAINER PAYSLIP (dynamic monthly via new admin controller)
  getTrainerPayslipDynamic({ userEmail, month, year }) {
    const query = buildQuery({ userEmail, month, year });
    return request(`/api/admin/trainer-payslip?${query}`);
  },

  // ATTENDANCE SESSIONS (with coverage + reschedule merge)
  getAttendanceSessions({ userEmail, date }) {
    const query = buildQuery({ userEmail, date });
    return request(`/api/admin/sessions?${query}`);
  },

  // RESCHEDULE
  rescheduleStudent(payload) {
    return request("/api/admin/reschedule", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  // MANAGEMENT — monitoring absensi harian lintas cabang
  getManagementDaily({ year, month }) {
    const query = buildQuery({ year, month });
    return request(`/api/management/daily-attendance?${query}`);
  },

  // MANAGEMENT — Pemantauan Trainer: masa pengawasan & kepatuhan To-Do List.
  getPengawasan() {
    return request("/api/management/pengawasan");
  },

  tambahPengawasan(payload) {
    return request("/api/management/pengawasan", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  ubahPengawasan(id, payload) {
    return request(`/api/management/pengawasan/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  akhiriPengawasan(id) {
    return request(`/api/management/pengawasan/${encodeURIComponent(id)}/akhiri`, {
      method: "POST"
    });
  },

  getRincianPengawasan(id) {
    return request(`/api/management/pengawasan/${encodeURIComponent(id)}/rincian`);
  },

  // Isi satu to-do (hanya baca), lintas cabang.
  getTodoIsi(classId, date) {
    const query = buildQuery({ classId, date });
    return request(`/api/management/todo-isi?${query}`);
  },

  // MANAGEMENT — Trainer Schedule: jam mengajar & jam kosong tiap trainer, lintas cabang.
  // Tanpa year/month = bulan berjalan menurut server (WIB).
  getTrainerSchedule({ year, month } = {}) {
    const query = buildQuery({ year, month });
    return request(`/api/management/trainer-schedule${query ? `?${query}` : ""}`);
  },

  // ── Raport (docs/rancangan-adopsi-raport.md) ──
  // Status fitur (env REPORTS_ENABLED): menu Raport hanya tampil bila aktif.
  getRaportStatus() {
    return request("/api/reports/status");
  },

  getRaportBank() {
    return request("/api/reports/bank");
  },

  getRaportTerms() {
    return request("/api/reports/terms");
  },

  getRaportKelasSaya(term) {
    const query = buildQuery({ term });
    return request(`/api/reports/my-classes${query ? `?${query}` : ""}`);
  },

  getRaportKelas(classId, term) {
    const query = buildQuery({ term });
    return request(`/api/reports/classes/${encodeURIComponent(classId)}${query ? `?${query}` : ""}`);
  },

  // { term, classId, studentId, isian: [{ bulan, aspek, kolom, nilai }] }
  simpanNilaiRaport(payload) {
    return request("/api/reports/scores", { method: "PUT", body: JSON.stringify(payload) });
  },

  // { term, classId, studentId, bulan, aspek, pilihan }
  simpanKomentarRaport(payload) {
    return request("/api/reports/comments", { method: "PUT", body: JSON.stringify(payload) });
  },

  // Pratinjau raport satu murid (disusun server) + syarat terbit + riwayat terbitnya.
  getRaportPratinjau(classId, studentId, term) {
    const query = buildQuery({ term });
    return request(
      `/api/reports/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/report${query ? `?${query}` : ""}`
    );
  },

  // { term, classId, studentId } — isi raportnya disusun server dari database.
  terbitkanRaport(payload) {
    return request("/api/reports/snapshots", { method: "POST", body: JSON.stringify(payload) });
  },

  getRaportTerbit(id, classId) {
    const query = buildQuery({ classId });
    return request(`/api/reports/snapshots/${encodeURIComponent(id)}${query ? `?${query}` : ""}`);
  },

  // ── Raport: team leader cabang aktif ──
  getRaportProgres(term) {
    const query = buildQuery({ term });
    return request(`/api/reports/progress${query ? `?${query}` : ""}`);
  },

  getRaportProgresKelas(classId, term) {
    const query = buildQuery({ term });
    return request(`/api/reports/progress/classes/${encodeURIComponent(classId)}${query ? `?${query}` : ""}`);
  },

  getRaportTermTL() {
    return request("/api/reports/progress/terms");
  },

  getRaportTerbitTL(id) {
    return request(`/api/reports/progress/snapshots/${encodeURIComponent(id)}`);
  },

  batalkanRaport(id, alasan) {
    return request(`/api/reports/snapshots/${encodeURIComponent(id)}/cancel`, { method: "POST", body: JSON.stringify({ alasan }) });
  },

  // mulai = "YYYY-MM"
  ubahPeriodeTerm(termId, mulai) {
    return request(`/api/reports/terms/${encodeURIComponent(termId)}`, { method: "PUT", body: JSON.stringify({ mulai }) });
  },

  bukaKunciTerm(termId) {
    return request(`/api/reports/terms/${encodeURIComponent(termId)}/unlock`, { method: "POST" });
  },

  tetapkanPemegangRaport(classId, trainerId) {
    return request(`/api/reports/classes/${encodeURIComponent(classId)}/owner`, { method: "PUT", body: JSON.stringify({ trainerId }) });
  },

  pilihKelasUtamaRaport(termId, studentId, classId) {
    return request(
      `/api/reports/terms/${encodeURIComponent(termId)}/students/${encodeURIComponent(studentId)}/class`,
      { method: "PUT", body: JSON.stringify({ classId }) }
    );
  },

  // ── Manajemen Kelas (Fase 1) ──
  getClasses() {
    return request("/api/classes");
  },

  getClassDetail(id) {
    return request(`/api/classes/${id}`);
  },

  getClassTrainers() {
    return request("/api/classes/meta/trainers");
  },
  getClassSessions() {
    return request("/api/classes/meta/sessions");
  },
  // Pemantauan sistem (super admin) — pemeriksaan kesehatan data lintas cabang.
  getSystemHealth() {
    return request("/api/monitor/health");
  },
  // Pemantauan absensi harian. `date` = "YYYY-MM-DD"; kosong = hari ini menurut server.
  getDailyAttendance(date) {
    return request(`/api/monitor/daily${date ? `?date=${encodeURIComponent(date)}` : ""}`);
  },
  // Daftar program & level kelas — diambil dari server supaya tak ada dua daftar
  // yang bisa berselisih antara backend & frontend.
  getClassLevels() {
    return request("/api/classes/meta/levels");
  },

  backfillClasses(reset = false) {
    return request(`/api/classes/backfill${reset ? "?reset=1" : ""}`, { method: "POST" });
  },

  compareClassRoster({ year, month, day }) {
    return request(`/api/classes/roster-compare?year=${year}&month=${month}&day=${day}`);
  },

  createClass(payload) {
    return request("/api/classes", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },

  // `force` dipakai saat admin menegaskan perubahan sesi yang akan memecah riwayat absensi.
  updateClass(id, payload, { force = false } = {}) {
    return request(`/api/classes/${id}${force ? "?force=1" : ""}`, {
      method: "PUT",
      body: JSON.stringify(payload)
    });
  },

  // `force` hanya berlaku utk super admin (server yang memutuskan).
  deleteClass(id, { force = false } = {}) {
    return request(`/api/classes/${id}${force ? "?force=1" : ""}`, { method: "DELETE" });
  },

  // `bentrok` = jawaban admin bila murid masih di kelas lain pada jam yang sama:
  // "pindah" (keluarkan dari kelas lama) atau "tetap" (tetap di dua kelas).
  enrollClassStudents(id, studentIds, { bentrok } = {}) {
    return request(`/api/classes/${id}/students`, {
      method: "POST",
      body: JSON.stringify(bentrok ? { studentIds, bentrok } : { studentIds })
    });
  },

  unenrollClassStudent(id, studentId) {
    return request(`/api/classes/${id}/students/${encodeURIComponent(studentId)}`, {
      method: "DELETE"
    });
  },

  setClassSchedules(id, schedules) {
    return request(`/api/classes/${id}/schedules`, {
      method: "PUT",
      body: JSON.stringify({ schedules })
    });
  }
};